import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { toast } from 'sonner';
import { toApiError, useApi, type ConversationDetail } from '@/shared/api';
import { useAuth } from '@/shared/auth';

/**
 * Ein angesehener Thread gilt als gelesen — aber nicht, wenn ihn der StayOn-Support im Workspace
 * eines Kunden öffnet: dann bliebe eine Nachricht für den Kunden unbemerkt. Im Support-Modus gibt
 * es stattdessen die ausdrückliche Aktion, die der Hook zurückgibt.
 */
export function useThreadMarkRead(
  threadId: string | null,
  thread: ConversationDetail | null | undefined,
  enabled = true,
): (() => void) | undefined {
  const api = useApi();
  const queryClient = useQueryClient();
  const supportSession = useAuth().support !== null;
  const unread = enabled && threadId !== null && (thread?.unreadCount ?? 0) > 0;
  const { mutate } = useMutation({
    mutationFn: (id: string) => api.messaging.markAsRead(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['inbox'] }),
    // Das automatische Markieren blieb schon immer still; nur die ausdrückliche Aktion meldet sich.
    onError: async (error) => {
      if (!supportSession) return;
      const problem = await toApiError(error);
      toast.error(`Nicht als gelesen markiert: ${problem.detail ?? problem.title}`);
    },
  });

  useEffect(() => {
    if (unread && !supportSession && threadId) mutate(threadId);
  }, [unread, supportSession, threadId, mutate, thread]);

  return supportSession && unread && threadId ? () => mutate(threadId) : undefined;
}
